import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Settings2, Save, RotateCcw, ArrowLeft, AlertTriangle } from 'lucide-react';

export default function TallyMapping() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiGet('/tally/config')
      .then((r) => { setRows(r.ledgers || []); setEdits({}); })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const valueOf = (row) => (edits[row.key] !== undefined ? edits[row.key] : row.tally_ledger_name);
  const dirty = rows.some((r) => edits[r.key] !== undefined && edits[r.key] !== r.tally_ledger_name);

  const save = async () => {
    // only send rows the user actually changed (non-empty)
    const payload = {};
    rows.forEach((r) => {
      if (edits[r.key] !== undefined && edits[r.key].trim() && edits[r.key] !== r.tally_ledger_name) {
        payload[r.key] = edits[r.key].trim();
      }
    });
    if (Object.keys(payload).length === 0) { toast.info('No changes to save'); return; }
    setSaving(true);
    try {
      const r = await apiPut('/tally/config', payload);
      setRows(r.ledgers || []);
      setEdits({});
      toast.success('Tally ledger mapping saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed (need Settings write permission)');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 p-6 md:p-8 max-w-4xl" data-testid="tally-mapping-page">
      <button onClick={() => nav('/app/finance/tally-export')} data-testid="back-to-export" className="text-xs text-slate-500 hover:text-[#3b4872] flex items-center gap-1.5"><ArrowLeft size={13} /> Back to Tally Export</button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2 flex items-center gap-2"><Settings2 size={13} /> Finance &amp; Accounts</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Tally Ledger Mapping</h1>
          <p className="text-sm text-slate-600 mt-1">Map each Ellora account head to the <strong>exact ledger name</strong> in your Tally company file.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading || saving} data-testid="mapping-reset" className="px-3 py-2 border border-slate-200 rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50"><RotateCcw size={13} /> Reload</button>
          <button onClick={save} disabled={!dirty || saving} data-testid="mapping-save" className="px-4 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-1.5 disabled:opacity-50"><Save size={13} /> {saving ? 'Saving…' : 'Save Mapping'}</button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="mapping-warning">
        <AlertTriangle size={15} className="mt-0.5 shrink-0" />
        <span>Ledger names are <strong>case- and spelling-sensitive</strong> and must match your Tally company file <strong>exactly</strong>. A mismatch makes Tally create a brand-new (stray) ledger on import, splitting your books. Confirm these with your accounts team before the first export.</span>
      </div>

      {loading ? <div className="text-slate-500 text-sm">Loading…</div> : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500"><tr>
              {['Ellora Account Head', 'Default Ledger', 'Your Tally Ledger Name'].map((h) => <th key={h} className="text-left px-3 py-2.5 font-display font-bold uppercase tracking-wider text-[10px]">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => {
                const changed = edits[row.key] !== undefined && edits[row.key] !== row.tally_ledger_name;
                const usingDefault = valueOf(row) === row.default;
                return (
                  <tr key={row.key} data-testid={`mapping-row-${row.key}`} className={changed ? 'bg-indigo-50/40' : ''}>
                    <td className="px-3 py-2 font-display font-bold text-slate-700">{row.label}</td>
                    <td className="px-3 py-2 text-slate-400 font-mono">{row.default}</td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={valueOf(row)}
                        onChange={(e) => setEdits((p) => ({ ...p, [row.key]: e.target.value }))}
                        data-testid={`mapping-input-${row.key}`}
                        className={`w-full max-w-xs border rounded-sm px-2.5 py-1.5 text-xs ${changed ? 'border-[#3b4872] bg-white' : usingDefault ? 'border-slate-200' : 'border-emerald-300 bg-emerald-50/40'}`}
                        placeholder={row.default}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-slate-400">Tip: in Tally, go to <em>Gateway of Tally → Chart of Accounts → Ledgers</em> to copy the exact names.</p>
    </div>
  );
}
